// URL-hash-backed room pointer (`#room=CODE&token=TOKEN`). Lets a shared
// link or a refreshed tab carry the room code (and, for old links, a legacy
// session token) without a server round-trip.

export interface RoomHashEntry {
  readonly roomCode: string;
  /**
   * A pre-existing `#room=CODE&token=TOKEN` link (old share links, browser
   * history, bookmarks). No longer trusted as an identity — the caller
   * seeds it into SessionStore so resume() can offer it to resolveSeat as
   * just one more candidate token, same as any other stored session.
   */
  readonly legacyToken?: string;
}

// Deliberately no room-code length/alphabet validation — a future milestone
// changes room codes from 4 to 6 characters, and a length check here would
// be a silent breakage on a cross-milestone rebase.
export function parseRoomHash(hash: string): RoomHashEntry | null {
  if (!hash || hash.startsWith("?")) return null;
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!trimmed) return null;
  const params = new URLSearchParams(trimmed);
  const rawRoomCode = params.get("room");
  if (!rawRoomCode) return null;
  // Uppercased here so every consumer (SessionStore's key, resume()'s own
  // uppercasing) agrees — a lowercase link would otherwise save a legacy
  // token under a key resume()'s lookup can never match.
  const roomCode = rawRoomCode.toUpperCase();
  const legacyToken = params.get("token");
  return legacyToken ? { roomCode, legacyToken } : { roomCode };
}

export function readRoomHash(): RoomHashEntry | null {
  try {
    return parseRoomHash(window.location.hash);
  } catch {
    return null;
  }
}

export function writeRoomHash(roomCode: string): void {
  try {
    const newHash = `#room=${encodeURIComponent(roomCode)}`;
    if (window.location.hash !== newHash) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
    }
  } catch {
    // history API not available — ignore
  }
}

export function clearRoomHash(): void {
  try {
    if (window.location.hash) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  } catch {
    // ignore
  }
}
